import React, { useCallback, useEffect } from "react";
// LIBRARIES
import { fetchAuthSession, signOut } from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { version } from "../../package.json";
import { fetchEntitiesQuery } from "../manager/apis/entities";

// MATERIAL
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

// ICONS
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import LocationOnIcon from "@mui/icons-material/LocationOn";

// CONTEXT
// import { useLocationContext } from "./LocationContext";
import { useUserContext } from "../contexts/UserContext";
import { usePosition } from "../hooks/usePosition";

const commonMenuItems = ["Account", "Invite", "Refresh Location", "Logout"];
const partnerMenuItems = ["Account", "Invite", "Refresh Location", "Download Provision Application", "Logout"];

function AppToolbar() {
    const { user, updateUser } = useUserContext();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [anchorElMenu, setAnchorElMenu] = React.useState(null);
    const { positionError, hasPermission } = usePosition();
    const { data: entities } = fetchEntitiesQuery();

    const scanseqrLogo = new URL("../assets/logo192.png", import.meta.url).href;

    useEffect(() => {
        async function currentSession() {
            try {
                const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
                if (!idToken || !idToken.payload) return;

                const entityList = Array.isArray(entities) ? entities : [];
                let lowestUserType = 5;
                if (entityList.length > 0) {
                    lowestUserType =
                        entityList.reduce((prev, curr) => (prev.userTypeId < curr.userTypeId ? prev : curr))
                            ?.userTypeId || 5;
                }

                updateUser({
                    ...idToken.payload,
                    accessToken: accessToken,
                    idToken: idToken,
                    identityId: accessToken.payload.sub,
                    entities: entityList,
                    lowestUserType: lowestUserType,
                });
                return;
            } catch (err) {
                console.log(err);
            }
        }
        currentSession();
    }, [entities]);

    const menuItems = user?.lowestUserType < 4 ? partnerMenuItems : commonMenuItems;

    const handleOpenUserMenu = (event) => {
        setAnchorElMenu(event.currentTarget);
    };

    const handleCloseUserMenu = async (menuItem) => {
        setAnchorElMenu(null);
        switch (menuItem) {
            case "Logout":
                signOut();
                break;
            case "Account":
                navigate("/account");
                break;
            case "Invite":
                navigate("/invite");
                break;
            case "Refresh Location":
                requestGeo();
                break;
            case "Download Provision Application":
                navigate("/provision-download");
                break;
            default:
                break;
        }
    };

    const geo = navigator.geolocation;

    const onError = useCallback(
        (error) => {
            console.log("LOCATION ERROR", error);
            enqueueSnackbar("GeoLocation not available.", { variant: "error" });
        },
        [enqueueSnackbar]
    );

    const onSuccess = useCallback(
        (data) => {
            console.log("LOCATION", data.coords);
            enqueueSnackbar(
                `GeoLocation available.(${data.coords.latitude.toFixed(5)},${data.coords.longitude.toFixed(5)})`,
                { variant: "success" }
            );
        },
        [enqueueSnackbar]
    );

    const requestGeo = () => {
        geo.getCurrentPosition(onSuccess, onError, {
            enableHighAccuracy: false,
            maximumAge: 15000,
            timeout: 3000,
        });
        console.log("REQUESTING NAV SERVICES");
    };

    const goHome = () => {
        navigate("/");
    };

    // Helper function to detect iOS Safari
    const isIOSSafari = () => {
        const ua = navigator.userAgent;
        const iOS = /iPad|iPhone|iPod/.test(ua);
        const webkit = /WebKit/.test(ua);
        return iOS && webkit && !/(CriOS|FxiOS|OPiOS|mercury)/i.test(ua);
    };

    // Helper function to detect if permissions API is available and reliable
    const canUsePermissionsAPI = () => {
        return (
            !isIOSSafari() &&
            navigator.permissions &&
            navigator.permissions.query &&
            typeof navigator.permissions.query === "function"
        );
    };

    const handlePositionError = () => {
        if (canUsePermissionsAPI()) {
            navigator.permissions
                .query({ name: "geolocation" })
                .then((result) => {
                    if (result.state === "denied") {
                        // If permission is denied, prompt user to enable in settings
                        enqueueSnackbar("Please enable location services in your browser settings", {
                            variant: "warning",
                        });
                    } else {
                        // Request geolocation permission
                        navigator.geolocation.getCurrentPosition(
                            (position) => {
                                console.log("Location permission granted");
                                onSuccess(position);
                            },
                            (error) => {
                                console.log("Location permission error:", error);
                                onError(error);
                            }
                        );
                    }
                })
                .catch((permissionError) => {
                    console.log("Permissions API failed, falling back to direct geolocation", permissionError);
                    // Fallback to direct geolocation call
                    navigator.geolocation.getCurrentPosition(
                        (position) => onSuccess(position),
                        (error) => onError(error)
                    );
                });
        } else {
            // For iOS Safari and other browsers without reliable permissions API
            // Try getting position directly and handle permission through error codes
            navigator.geolocation.getCurrentPosition(
                (position) => onSuccess(position),
                (error) => {
                    if (error.code === 1) {
                        // PERMISSION_DENIED
                        enqueueSnackbar("Please enable location services in your browser settings", {
                            variant: "warning",
                        });
                    }
                    onError(error);
                }
            );
        }
    };

    return (
        <AppBar position="static" color="secondary">
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    <Avatar
                        variant="square"
                        sx={{ display: "flex", mr: 1 }}
                        alt="S"
                        onClick={goHome}
                        src={scanseqrLogo} // TODO Get a smaller icon
                    />
                    <Typography
                        variant="h5"
                        noWrap
                        component="a"
                        href="/"
                        sx={{
                            mr: 2,
                            display: "flex",
                            flexGrow: 1,
                            color: "inherit",
                            textDecoration: "none",
                        }}
                    >
                        ScanseQR
                    </Typography>
                    <Box sx={{ flexGrow: 0, mr: 2 }}>
                        <Tooltip title={positionError?.message || "Location On"}>
                            <IconButton onClick={() => handlePositionError()} sx={{ p: 0 }}>
                                {!hasPermission || positionError ? (
                                    <LocationOffIcon color="primary" />
                                ) : (
                                    <LocationOnIcon color="primary" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ flexGrow: 0 }}>
                        <Tooltip title="Open settings">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <MoreVertIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: "45px" }}
                            id="menu-appbar"
                            anchorEl={anchorElMenu}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorElMenu)}
                            onClose={handleCloseUserMenu}
                        >
                            {menuItems.map((menuItem) => (
                                <MenuItem key={menuItem} onClick={() => handleCloseUserMenu(menuItem)}>
                                    <Typography textAlign="center">{menuItem}</Typography>
                                </MenuItem>
                            ))}
                            <Divider />
                            <MenuItem disabled dense>
                                <Typography variant="caption" color="text.secondary">
                                    Version {version}
                                </Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default AppToolbar;
